import React, {useState} from "react"
import styled, {css} from "styled-components"
import ReactHeight from "react-height"
import FormattedMessage from "@irati/utils/lang"
import Flex from "@irati/ui/src/Flex"
import {Hr} from "@irati/ui/src/Layout"
import {h6} from "@irati/ui/src/Typography/Heading/styles"

const Wrapper = styled.div<{isShowingMore: boolean; height: number; maxHeight: number}>`
  overflow: hidden;

  ${({isShowingMore, maxHeight, height, theme: {mediaQueries}}) => css`
    max-height: ${isShowingMore ? maxHeight : height}px;

    ${mediaQueries.minWidthMedia.tablet`
          transition: max-height ${isShowingMore ? "0.3s" : "0.5s"} ease-in-out;
        `}
  `}
`

interface ExpanderProps {
  isShowingMore: boolean
  onClick: () => void
}

interface Props {
  children: React.ReactNode
  /** The height at which this component will collapse down to, and begin showing the `Expander` component */
  height: number
  className?: string
  /** The Component to display for expanding the section, defaults to a 'show more' horizontal rule */
  Expander?: (props: ExpanderProps) => JSX.Element
  /**
   * The Component to display if the children are smaller than the provided `height` prop,
   * defaults to a <hr> but you can remove it entirely by passing an empty component */
  NonExpander?: () => JSX.Element | null
}

export default function ShowMore({height, children, className, Expander = DefaultExpander, NonExpander = DefaultNonExpander}: Props) {
  const [isShowingMore, setIsShowingMore] = useState(false)
  const [displayShowMoreText, setDisplayShowMoreText] = useState(false)
  const [maxHeight, setMaxHeight] = useState(height * 2)

  return (
    <div className={className}>
      <ReactHeight
        getElementHeight={(el: any) =>
          /* we grab the height of the element, including any hidden parts. This allows us
           * to update the value if the component shrinks vertically (and thus needs to take
           * up more horizontal space). This is a best effort, and won't resize if the component widens.
           * TODO: do more clever things to update the values for this use case */
          el.scrollHeight
        }
        onHeightReady={(h: number) => {
          if (h > height) {
            setDisplayShowMoreText(true)
          }
          if (h > maxHeight) {
            setMaxHeight(h)
          }
        }}>
        {displayShowMoreText ? (
          <Wrapper isShowingMore={isShowingMore} height={height} maxHeight={maxHeight}>
            {children}
          </Wrapper>
        ) : (
          <>{children}</>
        )}
      </ReactHeight>
      {displayShowMoreText ? <Expander isShowingMore={isShowingMore} onClick={() => setIsShowingMore(!isShowingMore)} /> : <NonExpander />}
    </div>
  )
}

const Text = styled.span`
  ${h6}
  text-transform: uppercase;
  cursor: pointer;
  flex-shrink: 0;

  ${({theme: {colors, spacing}}) => css`
    color: ${colors.greys.mediumDark};
    margin: 0 ${spacing.two};

    & > i {
      padding-left: ${spacing.half};
    }
  `}
`

function DefaultExpander({isShowingMore, onClick}: ExpanderProps) {
  return (
    <Flex marginTop={2} alignItems="center" onClick={onClick}>
      <Hr flush />
      <Text>
        <FormattedMessage id={isShowingMore ? "platform.components.showMore.showLess" : "platform.components.showMore.showMore"} />
        <i className={isShowingMore ? "fa fa-chevron-up" : "fa fa-chevron-down"} />
      </Text>
      <Hr flush />
    </Flex>
  )
}

function DefaultNonExpander() {
  return <Hr flush />
}
